import React from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { graphql, useFragment } from 'react-relay';
import { ButtonItem, HeadingItem, MenuGroup, Section } from '@atlaskit/menu';
import { useIntl } from '@atlassian/jira-intl';
import type { versionList_softwareReleasesMoveIssuesBetweenVersions_jiraIssue$key } from '@atlassian/jira-relay/src/__generated__/versionList_softwareReleasesMoveIssuesBetweenVersions_jiraIssue.graphql';
import type { versionList_softwareReleasesMoveIssuesBetweenVersions_jiraVersion$key } from '@atlassian/jira-relay/src/__generated__/versionList_softwareReleasesMoveIssuesBetweenVersions_jiraVersion.graphql';
import type { versionList_softwareReleasesMoveIssuesBetweenVersions_jiraVersionEdges$key } from '@atlassian/jira-relay/src/__generated__/versionList_softwareReleasesMoveIssuesBetweenVersions_jiraVersionEdges.graphql';
import { IssueWarning } from './issue-warning/index.tsx';
import messages from './messages.tsx';
import { MoveIssueToOtherVersion } from './move-issue-to-other-version/index.tsx';
import { RemoveIssueFromVersion } from './remove-issue-from-version/index.tsx';

type Props = {
	jiraVersionEdges: versionList_softwareReleasesMoveIssuesBetweenVersions_jiraVersionEdges$key;
	jiraIssue: versionList_softwareReleasesMoveIssuesBetweenVersions_jiraIssue$key;
	jiraVersion: versionList_softwareReleasesMoveIssuesBetweenVersions_jiraVersion$key;
	onVersionPickerModalOpen: () => void;
};

export const VersionList = ({
	jiraVersionEdges,
	jiraIssue,
	jiraVersion,
	onVersionPickerModalOpen,
}: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const versions = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment versionList_softwareReleasesMoveIssuesBetweenVersions_jiraVersionEdges on JiraVersionEdge
			@relay(plural: true) {
				node @required(action: NONE) {
					versionId @required(action: NONE)
					...moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraVersion
				}
			}
		`,
		jiraVersionEdges,
	);

	const issue = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment versionList_softwareReleasesMoveIssuesBetweenVersions_jiraIssue on JiraIssue {
				id @required(action: THROW)
				...moveIssueToOtherVersion_softwareReleasesMoveIssuesBetweenVersions_jiraIssue
				...removeIssueFromVersion_softwareReleasesMoveIssuesBetweenVersions
				...issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraIssue
			}
		`,
		jiraIssue,
	);

	const version = useFragment(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			fragment versionList_softwareReleasesMoveIssuesBetweenVersions_jiraVersion on JiraVersion {
				id @required(action: THROW)
				versionId @required(action: THROW)
				canAddAndRemoveIssues
				...issueWarning_softwareReleasesMoveIssuesBetweenVersions_jiraVersion
			}
		`,
		jiraVersion,
	);

	const originalJiraVersion = version.id;
	const allVersions = versions
		.filter(Boolean)
		.filter((object) => object.node.versionId !== version.versionId);

	return (
		<MenuGroup maxWidth={221}>
			<IssueWarning jiraIssue={issue} jiraVersion={version} />
			{!!version.canAddAndRemoveIssues && allVersions.length ? (
				<Section hasSeparator>
					<HeadingItem>{formatMessage(messages.versionsListHeader)}</HeadingItem>
					{allVersions.map((edge) => (
						<MoveIssueToOtherVersion
							originalJiraVersion={originalJiraVersion}
							jiraVersion={edge.node}
							key={edge.node.versionId}
							jiraIssue={issue}
						/>
					))}
					<ButtonItem onClick={onVersionPickerModalOpen}>
						{formatMessage(messages.otherVersionsButton)}
					</ButtonItem>
				</Section>
			) : null}
			{!!version.canAddAndRemoveIssues && (
				<RemoveIssueFromVersion originalJiraVersion={originalJiraVersion} jiraIssue={issue} />
			)}
		</MenuGroup>
	);
};
